import React, { useState } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./TripAI.css";
import MultiSelectModal from "./MultiSelectModal";
import Common from "../../Common/Common";
import { DateTime } from "luxon";
import { MAX_TRIP_DAYS, overlapError, totalDaySpan, tripLengthError } from "../../Common/DestinationValidation";
import { countries } from "../../Data/Countries";
import { citiesByCountry } from "../../Data/Cities";

// Shared "Explore Destinations" flow — extracted from TripAI.js so any
// multi-destination entry point (TripAI's own, and BuildMyTripWizard's "Build
// My Trip") can generate/review a multi-city itinerary without duplicating
// this logic. TripAI.js still has its own original inline copy for now —
// migrating it to use this component too is a separate, deliberately
// deferred follow-up (that's an edit to an already-shipped, heavily-used
// feature, and shouldn't be bundled into adding a new consumer).
//
// Two phases, both rendered as tai-paste-overlay/tai-paste-modal popups (same
// pattern TripAI.js uses): "country" (pick countries, trip dates, a nights-
// per-city starting point, and optional pinned start/end cities) generates a
// candidate list via Common.orderCitiesByNearestNeighbor +
// Common.allocateDaysByPopularity, then "review" (reorder, edit days per
// city, swap a city, add/remove) lets the user adjust before applying.
//
// Props: onApply(destinations) — destinations is
// [{id, city, country, dateStart, dateEnd}] (dateStart/dateEnd are Luxon
// DateTime, UTC-tagged wall-clock, same convention as everywhere else in the
// app); onCancel() — flow abandoned at any point, nothing applied.
const ExploreDestinationsFlow = ({ onApply, onCancel }) => {
  const GuideSettings = useSelector((state) => state.GuideSettings);
  const dayStartHour = GuideSettings?.schedule_day_start ?? 9;
  const dayEndHour = GuideSettings?.schedule_day_end ?? 20;

  const [phase, setPhase] = useState("country"); // "country" | "review"
  const [exploreSelectedCountries, setExploreSelectedCountries] = useState([]);
  // UTC-tagged (see the comment above) — DateTime.now() defaults to the
  // system's real local zone; left un-tagged, a user who never touches these
  // fields would feed a real-offset ISO string into the schedule build,
  // which gets misread as a genuine UTC conversion downstream and shifts
  // every event's time by their actual UTC offset.
  const [exploreStartDateTime, setExploreStartDateTime] = useState(() =>
    DateTime.now().setZone("utc", { keepLocalTime: true }).plus({ days: 1 }).set({ hour: dayStartHour, minute: 0 })
  );
  const [exploreEndDateTime, setExploreEndDateTime] = useState(() =>
    DateTime.now().setZone("utc", { keepLocalTime: true }).plus({ days: 8 }).set({ hour: dayEndHour, minute: 0 })
  );
  const [exploreStartCity, setExploreStartCity] = useState(null);
  const [exploreEndCity, setExploreEndCity] = useState(null);
  const [exploreMinNights, setExploreMinNights] = useState(2);
  // Off (default): arrival/departure are hard constraints — adding a night
  // to one city must come from another. On: departure just moves out (or
  // in) to match, so any city's nights can change freely.
  const [exploreDatesFlexible, setExploreDatesFlexible] = useState(false);
  const [exploreCandidates, setExploreCandidates] = useState([]);
  const [exploreTripStartDate, setExploreTripStartDate] = useState(null);
  const [exploreTripEndDate, setExploreTripEndDate] = useState(null);
  const [exploreError, setExploreError] = useState("");
  // Nights edits commit on blur/Enter, not per keystroke — clearing the box
  // to retype would otherwise read as 0 nights and remove the city, and
  // typing "10" over "3" would apply "1" first. Same as mobile.
  const [editingDaysId, setEditingDaysId] = useState(null);
  const [editingDaysText, setEditingDaysText] = useState("");
  // Local MultiSelect overlay — mirrors TripAI's own this.MultiSelect()
  // helper, just as component state instead of class state.
  const [multiSelect, setMultiSelect] = useState(null); // {items, selectedItems, mode, callback, title} | null

  const openMultiSelect = (items, selectedItems, mode, callback, title) => {
    setMultiSelect({ items, selectedItems, mode, callback, title });
  };

  const closeAll = () => {
    setPhase("country");
    setExploreCandidates([]);
    setExploreTripStartDate(null);
    setExploreTripEndDate(null);
    setExploreError("");
    onCancel();
  };

  const selectExploreCountries = () => {
    const available = countries.filter((c) => Array.isArray(citiesByCountry[c]) && citiesByCountry[c].length > 0).sort();
    const items = available.map((c, i) => ({ label: c, name: c, value: c, id: (i + 1).toString() }));
    const selectedItems = items.filter((it) => exploreSelectedCountries.includes(it.value));
    openMultiSelect(items, selectedItems, "multi", exploreCountriesSelected, "Select Countries (Explore Destinations)");
  };

  const exploreCountriesSelected = (selected) => {
    const nextCountries = selected.map((s) => s.value);
    setExploreSelectedCountries(nextCountries);
    // Changing the country list can invalidate a previously-pinned start/end
    // city (it belonged to a country that's no longer selected) — drop it
    // rather than silently generating against a stale pin.
    setExploreStartCity((prev) => (nextCountries.includes(prev?.country) ? prev : null));
    setExploreEndCity((prev) => (nextCountries.includes(prev?.country) ? prev : null));
  };

  // Union of every curated city across the currently-selected countries, for
  // the start/end city pickers — deliberately not limited to the
  // popularity-based picks generateExploreDestinations will make, since a
  // flight's arrival/departure city is fixed by the airline, not by how
  // popular that city is.
  const exploreCityChoiceItems = () => {
    const items = [];
    let id = 1;
    exploreSelectedCountries.forEach((country) => {
      (citiesByCountry[country] || []).forEach((c) => {
        items.push({ label: `${c.city}, ${country}`, name: c.city, value: `${c.city}||${country}`, id: (id++).toString() });
      });
    });
    return items;
  };

  const selectExploreStartCity = () => {
    if (exploreSelectedCountries.length === 0) {
      setExploreError("Select countries first.");
      return;
    }
    const items = exploreCityChoiceItems();
    const selectedItems = exploreStartCity ? items.filter((it) => it.value === `${exploreStartCity.city}||${exploreStartCity.country}`) : [];
    openMultiSelect(items, selectedItems, "uni", exploreStartCitySelected, "Starting City (optional — e.g. your arrival flight)");
  };

  const exploreStartCitySelected = (selected) => {
    if (!selected || selected.length === 0) {
      setExploreStartCity(null);
      return;
    }
    const [city, country] = selected[0].value.split("||");
    const cityData = (citiesByCountry[country] || []).find((c) => c.city === city);
    setExploreStartCity(cityData ? { ...cityData, country } : null);
  };

  const selectExploreEndCity = () => {
    if (exploreSelectedCountries.length === 0) {
      setExploreError("Select countries first.");
      return;
    }
    const items = exploreCityChoiceItems();
    const selectedItems = exploreEndCity ? items.filter((it) => it.value === `${exploreEndCity.city}||${exploreEndCity.country}`) : [];
    openMultiSelect(items, selectedItems, "uni", exploreEndCitySelected, "Ending City (optional — e.g. your departure flight)");
  };

  const exploreEndCitySelected = (selected) => {
    if (!selected || selected.length === 0) {
      setExploreEndCity(null);
      return;
    }
    const [city, country] = selected[0].value.split("||");
    const cityData = (citiesByCountry[country] || []).find((c) => c.city === city);
    setExploreEndCity(cityData ? { ...cityData, country } : null);
  };

  // Re-derives every candidate's dateStart/dateEnd from the current array
  // order and each one's own `.days`. Arrival and departure are hard
  // constraints: the first *selected* candidate's dateStart is always
  // tripStart and the last *selected* candidate's dateEnd is always tripEnd,
  // exact time-of-day included (a late arrival or early departure genuinely
  // shrinks that day). Safe to force unconditionally because every caller
  // has already gone through rebalanceExploreDays (or generation's own
  // allocateDaysByPopularity), which guarantees the selected days always sum
  // to exactly the fixed trip length. Unselected candidates don't consume
  // trip time — placed at wherever the cursor sits without advancing it.
  const rechainExploreDates = (candidates, tripStart, tripEnd) => {
    if (candidates.length === 0) return candidates;
    let lastSelectedIdx = -1;
    for (let i = candidates.length - 1; i >= 0; i--) {
      if (candidates[i].selected) { lastSelectedIdx = i; break; }
    }
    let cursor = (tripStart || candidates[0].dateStart).set({ hour: dayStartHour, minute: 0 });
    let firstSelectedSeen = false;
    return candidates.map((c, i) => {
      if (!c.selected) {
        return { ...c, dateStart: cursor, dateEnd: cursor.plus({ days: c.days - 1 }).set({ hour: dayEndHour, minute: 0 }) };
      }
      const dateStart = firstSelectedSeen ? cursor : (tripStart || cursor);
      firstSelectedSeen = true;
      const dateEnd = i === lastSelectedIdx && tripEnd ? tripEnd : dateStart.plus({ days: c.days - 1 }).set({ hour: dayEndHour, minute: 0 });
      cursor = dateEnd.plus({ days: 1 }).set({ hour: dayStartHour, minute: 0 });
      return { ...c, dateStart, dateEnd };
    });
  };

  // Sets candidate `id`'s days to `newDays`, taking (or giving back) the
  // difference from other SELECTED candidates so the total across all
  // selected candidates never changes. Pulls from the end of the trip first,
  // stopping at each donor's `minNights` floor; returns null (reject the
  // whole edit) if there isn't enough room to take from.
  const rebalanceExploreDays = (candidates, id, newDays, minNights) => {
    const idx = candidates.findIndex((c) => c.id === id);
    if (idx === -1) return null;
    const oldDays = candidates[idx].days;
    const delta = newDays - oldDays;
    if (delta === 0) return candidates;

    const next = candidates.map((c) => ({ ...c }));
    next[idx].days = newDays;

    const otherIndices = next
      .map((c, i) => i)
      .filter((i) => i !== idx && next[i].selected)
      .reverse(); // pull from the end of the trip first

    if (delta > 0) {
      let remaining = delta;
      for (const i of otherIndices) {
        if (remaining <= 0) break;
        const room = next[i].days - minNights;
        if (room <= 0) continue;
        const take = Math.min(room, remaining);
        next[i].days -= take;
        remaining -= take;
      }
      if (remaining > 0) return null; // nowhere left to take from — reject
    } else if (otherIndices.length > 0) {
      next[otherIndices[0]].days += -delta;
    } else {
      return null; // nothing else selected to absorb the reduction
    }
    return next;
  };

  const generateExploreDestinations = () => {
    if (exploreSelectedCountries.length === 0) {
      setExploreError("Select at least one country.");
      return;
    }
    if (exploreEndDateTime <= exploreStartDateTime) {
      setExploreError("End date/time must be after the start.");
      return;
    }
    const minNights = Math.max(1, Math.round(Number(exploreMinNights) || 1));
    const totalDays = totalDaySpan([{ dateStart: exploreStartDateTime, dateEnd: exploreEndDateTime }]);
    if (totalDays > MAX_TRIP_DAYS) {
      setExploreError(`That's a ${totalDays}-day span — maximum is ${MAX_TRIP_DAYS}.`);
      return;
    }
    const maxCities = Math.max(1, Math.floor(totalDays / minNights));

    // One country's worth of picks: its curated cities sorted by popularity,
    // capped by the minimum-nights floor so a short trip (or a high minimum)
    // doesn't fragment into more stops than can actually fit.
    const perCountryCap = Math.max(1, Math.floor(maxCities / exploreSelectedCountries.length) || maxCities);
    let picks = [];
    exploreSelectedCountries.forEach((country) => {
      const cityList = (citiesByCountry[country] || []).slice().sort((a, b) => b.popularity - a.popularity);
      cityList.slice(0, perCountryCap).forEach((c) => picks.push({ ...c, country }));
    });

    // A pinned start/end city (a flight's arrival/departure point) may not
    // have been popular enough to make the cut above — force it in rather
    // than silently ignoring the pin.
    const sameCity = (a, b) => !!a && !!b && a.city === b.city && a.country === b.country;
    [exploreStartCity, exploreEndCity].forEach((pinned) => {
      if (pinned && !picks.some((p) => sameCity(p, pinned))) picks.push({ ...pinned });
    });

    // Can't have more cities than the minimum-nights floor allows — trim
    // lowest-popularity picks first, but a pinned start/end city is never
    // trimmed (sorted to the front).
    if (picks.length > maxCities) {
      const isPinned = (p) => sameCity(p, exploreStartCity) || sameCity(p, exploreEndCity);
      picks.sort((a, b) => (isPinned(a) === isPinned(b) ? b.popularity - a.popularity : isPinned(a) ? -1 : 1));
      picks = picks.slice(0, maxCities);
    }
    if (picks.length === 0) {
      setExploreError("No curated cities available for the selected countries.");
      return;
    }

    // Must be reference-equal to an entry in `picks` for
    // orderCitiesByNearestNeighbor to recognize it as the pinned city.
    const fixedStart = exploreStartCity ? picks.find((p) => sameCity(p, exploreStartCity)) : null;
    const fixedEnd = exploreEndCity ? picks.find((p) => sameCity(p, exploreEndCity)) : null;

    const ordered = Common.orderCitiesByNearestNeighbor(picks, { fixedStart, fixedEnd });
    const dayCounts = Common.allocateDaysByPopularity(ordered, totalDays, minNights);

    const rawCandidates = ordered.map((c, i) => ({
      id: `explore_${i}`,
      city: c.city,
      country: c.country,
      dateStart: exploreStartDateTime, // placeholder — rechainExploreDates fills in real dates below
      dateEnd: exploreEndDateTime,
      days: dayCounts[i],
      selected: true,
    }));

    setExploreTripStartDate(exploreStartDateTime);
    setExploreTripEndDate(exploreEndDateTime);
    setExploreCandidates(rechainExploreDates(rawCandidates, exploreStartDateTime, exploreEndDateTime));
    setExploreError("");
    setPhase("review");
  };

  // Unchecking a city sets its days to 0 and gives those days to the last
  // remaining selected city — re-checking pulls a minimum-nights stay back
  // out of wherever there's room. Either way this goes through
  // rebalanceExploreDays so the grand total (and therefore arrival/
  // departure) never moves.
  const toggleExploreCandidate = (id) => {
    setExploreCandidates((prev) => {
      const target = prev.find((c) => c.id === id);
      if (!target) return prev;
      if (target.selected) {
        const freed = target.days;
        const others = prev.filter((c) => c.id !== id && c.selected);
        const receiverId = others.length > 0 ? others[others.length - 1].id : null;
        const next = prev.map((c) => {
          if (c.id === id) return { ...c, selected: false, days: 0 };
          if (c.id === receiverId) return { ...c, days: c.days + freed };
          return c;
        });
        setExploreError("");
        return rechainExploreDates(next, exploreTripStartDate, exploreTripEndDate);
      }
      // Re-checking: any city can go down to 1 night if the user is editing
      // by hand, so the floor used against OTHER cities here is always 1,
      // not exploreMinNights.
      const minNights = Math.max(1, Math.round(Number(exploreMinNights) || 1));
      const withSelection = prev.map((c) => (c.id === id ? { ...c, selected: true } : c));
      const rebalanced = rebalanceExploreDays(withSelection, id, minNights, 1);
      if (!rebalanced) {
        setExploreError("Not enough room to add this city back — every other city is already down to 1 night.");
        return prev;
      }
      setExploreError("");
      return rechainExploreDates(rebalanced, exploreTripStartDate, exploreTripEndDate);
    });
  };

  // Moves `delta` nights between two candidates: if delta > 0, `id` gains
  // and `partnerId` gives (must stay at 1+ night); if negative, `id` gives
  // and `partnerId` gains.
  const applyExploreDaysTransfer = (id, partnerId, delta) => {
    setExploreCandidates((prev) => {
      const next = prev.map((c) => ({ ...c }));
      const target = next.find((c) => c.id === id);
      const partner = next.find((c) => c.id === partnerId);
      if (!target || !partner) return prev;
      if (delta > 0) {
        if (partner.days - delta < 1) {
          setExploreError(`${partner.city} only has ${partner.days} night${partner.days === 1 ? "" : "s"} — can't give up ${delta}.`);
          return prev;
        }
        target.days += delta;
        partner.days -= delta;
      } else {
        target.days += delta;
        partner.days += -delta;
      }
      setExploreError("");
      return rechainExploreDates(next, exploreTripStartDate, exploreTripEndDate);
    });
  };

  // With "Dates are flexible" on, a day-count change doesn't need a donor
  // city at all — it just grows or shrinks the trip itself: adding a night
  // to any city pushes departure out by one, removing one pulls it back in.
  // Still capped at 21 days total.
  const applyExploreFlexibleDaysChange = (id, delta) => {
    const currentTotal = exploreCandidates.filter((c) => c.selected).reduce((s, c) => s + c.days, 0);
    const newTotal = currentTotal + delta;
    if (newTotal > MAX_TRIP_DAYS) {
      setExploreError(`The trip would be ${newTotal} days — maximum is ${MAX_TRIP_DAYS}.`);
      return;
    }
    const newTripEnd = exploreTripEndDate.plus({ days: delta });
    const next = exploreCandidates.map((c) => (c.id === id ? { ...c, days: c.days + delta } : c));
    setExploreTripEndDate(newTripEnd);
    setExploreCandidates(rechainExploreDates(next, exploreTripStartDate, newTripEnd));
    setExploreError("");
  };

  // Editing a candidate's day count takes/gives the difference from ONE
  // other selected city, chosen by the user, so arrival/departure never
  // move. Clearing the field or entering 0 removes the city entirely.
  const setExploreCandidateDays = (id, days) => {
    const value = Math.round(Number(days));
    if (!Number.isFinite(value) || value <= 0) {
      removeExploreCandidate(id);
      return;
    }
    const target = exploreCandidates.find((c) => c.id === id);
    if (!target) return;
    const clamped = Math.max(1, Math.min(21, value));
    const delta = clamped - target.days;
    if (delta === 0) return;

    if (exploreDatesFlexible) {
      applyExploreFlexibleDaysChange(id, delta);
      return;
    }

    const others = exploreCandidates.filter((c) => c.id !== id && c.selected);
    if (others.length === 0) {
      setExploreError("No other selected city to take/give days with.");
      return;
    }
    if (others.length === 1) {
      applyExploreDaysTransfer(id, others[0].id, delta);
      return;
    }
    const verb = delta > 0 ? "Take" : "Give";
    const prep = delta > 0 ? "from" : "to";
    const amount = Math.abs(delta);
    const items = others.map((c, i) => ({
      label: `${c.city}, ${c.country} (${c.days} night${c.days === 1 ? "" : "s"})`,
      name: c.city,
      value: c.id,
      id: (i + 1).toString(),
    }));
    openMultiSelect(
      items,
      [],
      "uni",
      (selected) => {
        if (!selected || selected.length === 0) return; // cancelled — leave days as they were
        applyExploreDaysTransfer(id, selected[0].value, delta);
      },
      `${verb} ${amount} night${amount === 1 ? "" : "s"} ${prep} which city?`
    );
  };

  // An empty box left empty reverts; an explicit "0" still removes the city.
  const commitEditingDays = (id) => {
    const raw = editingDaysText;
    setEditingDaysId(null);
    setEditingDaysText("");
    if (raw.trim() === "") return;
    setExploreCandidateDays(id, raw);
  };

  // Removes a candidate outright — its freed days go to the last remaining
  // selected city, same redistribution as unchecking.
  const removeExploreCandidate = (id) => {
    setExploreCandidates((prev) => {
      const target = prev.find((c) => c.id === id);
      if (!target) return prev;
      const freed = target.selected ? target.days : 0;
      const others = prev.filter((c) => c.id !== id && c.selected);
      const receiverId = others.length > 0 ? others[others.length - 1].id : null;
      const next = prev
        .filter((c) => c.id !== id)
        .map((c) => (c.id === receiverId ? { ...c, days: c.days + freed } : c));
      setExploreError("");
      return rechainExploreDates(next, exploreTripStartDate, exploreTripEndDate);
    });
  };

  // Lets the user swap a candidate for a different city in the same
  // country, without touching its day count, position, or dates.
  const selectExploreSubstituteCity = (id) => {
    const target = exploreCandidates.find((c) => c.id === id);
    if (!target) return;
    const alreadyUsed = new Set(exploreCandidates.filter((c) => c.id !== id).map((c) => `${c.city}||${c.country}`));
    const choices = (citiesByCountry[target.country] || []).filter((c) => !alreadyUsed.has(`${c.city}||${target.country}`));
    if (choices.length === 0) {
      setExploreError(`No other curated cities available for ${target.country}.`);
      return;
    }
    const items = choices.map((c, i) => ({ label: c.city, name: c.city, value: c.city, id: (i + 1).toString() }));
    openMultiSelect(items, [], "uni", (selected) => exploreSubstituteCitySelected(id, selected, choices), `Swap ${target.city} for…`);
  };

  const exploreSubstituteCitySelected = (id, selected, choices) => {
    if (!selected || selected.length === 0) return;
    const chosen = choices.find((c) => c.city === selected[0].value);
    if (!chosen) return;
    setExploreCandidates((prev) =>
      prev.map((c) => (c.id === id ? { ...c, city: chosen.city, lat: chosen.lat, lon: chosen.lon, popularity: chosen.popularity } : c))
    );
    setExploreError("");
  };

  // Pick any curated country, then a city in it not already in the list,
  // then source its nights the same way re-checking a previously-unchecked
  // city does (fixed-dates: pull from other selected cities; flexible:
  // extend the trip).
  const openExploreAddCity = () => {
    const items = countries
      .filter((c) => Array.isArray(citiesByCountry[c]) && citiesByCountry[c].length > 0)
      .sort()
      .map((c, i) => ({ label: c, name: c, value: c, id: (i + 1).toString() }));
    openMultiSelect(items, [], "uni", exploreAddCityCountrySelected, "Add a City — Pick a Country");
  };

  const exploreAddCityCountrySelected = (selected) => {
    if (!selected || selected.length === 0) return;
    const country = selected[0].value;
    const alreadyUsed = new Set(exploreCandidates.map((c) => `${c.city}||${c.country}`));
    const choices = (citiesByCountry[country] || []).filter((c) => !alreadyUsed.has(`${c.city}||${country}`));
    if (choices.length === 0) {
      setExploreError(`No other curated cities available for ${country}.`);
      return;
    }
    const items = choices.map((c, i) => ({ label: c.city, name: c.city, value: c.city, id: (i + 1).toString() }));
    openMultiSelect(items, [], "uni", (sel) => exploreAddCityCitySelected(sel, choices, country), `Add a City in ${country}`);
  };

  const exploreAddCityCitySelected = (selected, choices, country) => {
    if (!selected || selected.length === 0) return;
    const chosen = choices.find((c) => c.city === selected[0].value);
    if (!chosen) return;

    const minNights = Math.max(1, Math.round(Number(exploreMinNights) || 1));
    const newId = `explore_added_${Date.now()}`;
    // days starts at 0 (not minNights) so rebalanceExploreDays below sees a
    // real delta and actually sources the nights from elsewhere.
    const newCandidate = { id: newId, city: chosen.city, country, lat: chosen.lat, lon: chosen.lon, popularity: chosen.popularity, days: 0, selected: true };

    if (exploreDatesFlexible) {
      const currentTotal = exploreCandidates.filter((c) => c.selected).reduce((s, c) => s + c.days, 0);
      const newTotal = currentTotal + minNights;
      if (newTotal > MAX_TRIP_DAYS) {
        setExploreError(`The trip would be ${newTotal} days — maximum is ${MAX_TRIP_DAYS}.`);
        return;
      }
      const newTripEnd = exploreTripEndDate.plus({ days: minNights });
      const withNew = [...exploreCandidates, { ...newCandidate, days: minNights }];
      setExploreTripEndDate(newTripEnd);
      setExploreCandidates(rechainExploreDates(withNew, exploreTripStartDate, newTripEnd));
      setExploreError("");
      return;
    }

    const next = [...exploreCandidates, newCandidate];
    const rebalanced = rebalanceExploreDays(next, newId, minNights, 1);
    if (!rebalanced) {
      setExploreError("Not enough room to add this city — every other city is already down to 1 night.");
      return;
    }
    setExploreCandidates(rechainExploreDates(rebalanced, exploreTripStartDate, exploreTripEndDate));
    setExploreError("");
  };

  // The auto-generated order is a starting point, not the final word — pure
  // reorder, total days unaffected, so no rebalancing needed, just
  // rechaining.
  const moveExploreCandidate = (id, direction) => {
    setExploreCandidates((prev) => {
      const idx = prev.findIndex((c) => c.id === id);
      const swapWith = idx + direction;
      if (idx === -1 || swapWith < 0 || swapWith >= prev.length) return prev;
      const next = prev.slice();
      const tmp = next[idx];
      next[idx] = next[swapWith];
      next[swapWith] = tmp;
      return rechainExploreDates(next, exploreTripStartDate, exploreTripEndDate);
    });
  };

  const applyExploreDestinations = () => {
    const selected = exploreCandidates.filter((c) => c.selected);
    if (selected.length === 0) {
      setExploreError("Select at least one destination.");
      return;
    }
    const overlap = overlapError(selected);
    if (overlap) {
      setExploreError(overlap);
      return;
    }
    const lengthError = tripLengthError(selected);
    if (lengthError) {
      setExploreError(lengthError);
      return;
    }
    onApply(selected.map(({ id, city, country, dateStart, dateEnd }) => ({ id, city, country, dateStart, dateEnd })));
  };

  return (
    <>
      {multiSelect && (
        <MultiSelectModal
          show={true}
          onHide={() => setMultiSelect(null)}
          MultiselectItems={multiSelect.items}
          MultiselectSelectedItems={multiSelect.selectedItems}
          MultiselectMode={multiSelect.mode}
          MultiselectCallback={(selected) => { const cb = multiSelect.callback; setMultiSelect(null); cb(selected); }}
          MultiselectTitle={multiSelect.title}
          MultiselectSearchText=""
        />
      )}

      {!multiSelect && phase === "country" && (
        <div className="tai-paste-overlay" onClick={closeAll}>
          <div className="tai-paste-modal" onClick={(e) => e.stopPropagation()}>
            <p className="tai-paste-title">Explore Destinations</p>
            <p className="tai-paste-hint">
              Only know the country, not the cities? Pick one or more countries and a total trip
              length — the app will suggest cities, an order, and how many days to spend in each.
            </p>
            {exploreError !== "" && <p className="tai-paste-error">{exploreError}</p>}
            <div className="tai-row-item" onClick={selectExploreCountries}>
              <span className="tai-row-key">Countries</span>
              <span className="tai-row-value">
                {exploreSelectedCountries.length === 0 ? "Select…" : exploreSelectedCountries.join(", ")}
              </span>
              <FontAwesomeIcon icon={solid("chevron-right")} />
            </div>
            <div className="tai-date-row">
              <span className="tai-row-key">Arrival</span>
              <DatePicker showMonthDropdown showYearDropdown dropdownMode="select"
                selected={Common.JSDateFromLuxonDate(exploreStartDateTime)}
                onChange={(date) => setExploreStartDateTime(Common.LuxonDateFromJSDate(date))}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={5}
                timeCaption="time"
                dateFormat="MMM d, yyyy HH:mm"
                withPortal
              />
            </div>
            <div className="tai-date-row">
              <span className="tai-row-key">Departure</span>
              <DatePicker showMonthDropdown showYearDropdown dropdownMode="select"
                selected={Common.JSDateFromLuxonDate(exploreEndDateTime)}
                onChange={(date) => setExploreEndDateTime(Common.LuxonDateFromJSDate(date))}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={5}
                timeCaption="time"
                dateFormat="MMM d, yyyy HH:mm"
                withPortal
              />
            </div>
            <p className="tai-paste-hint">
              Use your actual flight arrival/departure date and time — a late arrival or early
              departure leaves less time for activities on those days. If your flights are already
              booked, pin the arrival/departure cities below too — otherwise leave them blank and any
              city works.
            </p>
            <p className="tai-paste-hint">
              A starting suggestion for how many nights to spend per city — you can freely change
              any city's nights afterward, this just shapes the first attempt.
            </p>
            <div className="tai-input-row">
              <span className="tai-input-label">Suggested Nights per City</span>
              <input
                className="tai-input-field"
                type="number"
                min="1"
                max="21"
                value={exploreMinNights}
                onChange={(e) => setExploreMinNights(e.target.value)}
              />
            </div>
            <label className="tai-explore-flex-toggle">
              <input
                type="checkbox"
                checked={exploreDatesFlexible}
                onChange={(e) => setExploreDatesFlexible(e.target.checked)}
              />
              <span>
                Dates are flexible — adding a night to a city lengthens the trip (and removing one
                shortens it) instead of taking nights from another city
              </span>
            </label>
            <div className="tai-row-item" onClick={selectExploreStartCity}>
              <span className="tai-row-key">Starting City</span>
              <span className="tai-row-value">
                {exploreStartCity ? `${exploreStartCity.city}, ${exploreStartCity.country}` : "Any"}
              </span>
              {exploreStartCity && (
                <button className="tai-explore-clear-btn" onClick={(e) => { e.stopPropagation(); setExploreStartCity(null); }}>
                  <FontAwesomeIcon icon={solid("xmark")} />
                </button>
              )}
              <FontAwesomeIcon icon={solid("chevron-right")} />
            </div>
            <div className="tai-row-item" onClick={selectExploreEndCity}>
              <span className="tai-row-key">Ending City</span>
              <span className="tai-row-value">
                {exploreEndCity ? `${exploreEndCity.city}, ${exploreEndCity.country}` : "Any"}
              </span>
              {exploreEndCity && (
                <button className="tai-explore-clear-btn" onClick={(e) => { e.stopPropagation(); setExploreEndCity(null); }}>
                  <FontAwesomeIcon icon={solid("xmark")} />
                </button>
              )}
              <FontAwesomeIcon icon={solid("chevron-right")} />
            </div>
            <div className="tai-paste-actions">
              <button className="tai-cancel-btn" onClick={closeAll}>Cancel</button>
              <button className="tai-primary-btn" onClick={generateExploreDestinations}>Generate</button>
            </div>
          </div>
        </div>
      )}

      {!multiSelect && phase === "review" && (
        <div className="tai-paste-overlay" onClick={closeAll}>
          <div className="tai-paste-modal" onClick={(e) => e.stopPropagation()}>
            <p className="tai-paste-title">Explore Destinations</p>
            <p className="tai-paste-hint">
              Suggested cities, ordered by distance. Uncheck any you don't want, or adjust days per city.
            </p>
            {exploreError !== "" && <p className="tai-paste-error">{exploreError}</p>}
            <button className="tai-paste-dest-btn" style={{ marginBottom: 8, flex: "0 0 auto" }} onClick={openExploreAddCity}>
              <FontAwesomeIcon icon={solid("circle-plus")} />
              Add City
            </button>
            <div className="tai-schedule-candidates">
              {exploreCandidates.map((c, idx) => (
                <div key={c.id} className="tai-schedule-candidate-row">
                  <div className="tai-explore-reorder-col">
                    <button
                      className="tai-explore-reorder-btn"
                      disabled={idx === 0}
                      onClick={() => moveExploreCandidate(c.id, -1)}
                      aria-label={`Move ${c.city} earlier`}
                    >
                      <FontAwesomeIcon icon={solid("chevron-up")} />
                    </button>
                    <button
                      className="tai-explore-reorder-btn"
                      disabled={idx === exploreCandidates.length - 1}
                      onClick={() => moveExploreCandidate(c.id, 1)}
                      aria-label={`Move ${c.city} later`}
                    >
                      <FontAwesomeIcon icon={solid("chevron-down")} />
                    </button>
                  </div>
                  <input type="checkbox" checked={c.selected} onChange={() => toggleExploreCandidate(c.id)} />
                  <span className="tai-schedule-candidate-label">{c.city}, {c.country}</span>
                  <input
                    className="tai-explore-days-input"
                    type="number"
                    min="1"
                    max="21"
                    value={editingDaysId === c.id ? editingDaysText : c.days}
                    onFocus={(e) => { setEditingDaysId(c.id); setEditingDaysText(String(c.days)); e.target.select(); }}
                    onChange={(e) => setEditingDaysText(e.target.value)}
                    onBlur={() => commitEditingDays(c.id)}
                    onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                  />
                  <span className="tai-schedule-candidate-dates">
                    {c.dateStart.toFormat("LLL d")} – {c.dateEnd.toFormat("LLL d, yyyy")}
                  </span>
                  <button
                    className="tai-explore-icon-btn"
                    onClick={() => selectExploreSubstituteCity(c.id)}
                    aria-label={`Swap ${c.city} for a different city`}
                    title="Swap city"
                  >
                    <FontAwesomeIcon icon={solid("arrows-rotate")} />
                  </button>
                  <button
                    className="tai-explore-icon-btn tai-explore-icon-btn-danger"
                    onClick={() => removeExploreCandidate(c.id)}
                    aria-label={`Remove ${c.city}`}
                    title="Remove"
                  >
                    <FontAwesomeIcon icon={solid("trash")} />
                  </button>
                </div>
              ))}
            </div>
            <div className="tai-paste-actions">
              <button className="tai-cancel-btn" onClick={closeAll}>Cancel</button>
              <button
                className="tai-cancel-btn"
                onClick={() => {
                  // Countries/dates/pinned cities/min-nights all stay in
                  // state untouched — reopening the country phase picks up
                  // right where the user left off, ready to tweak and
                  // regenerate.
                  setPhase("country");
                  setExploreCandidates([]);
                  setExploreTripStartDate(null);
                  setExploreTripEndDate(null);
                  setExploreError("");
                }}
              >
                <FontAwesomeIcon icon={solid("arrow-left")} />
                Back
              </button>
              <button className="tai-primary-btn" onClick={applyExploreDestinations}>Use Selected</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExploreDestinationsFlow;
